/**
 * SuperAdminEmailTemplatesPage
 *
 * - Left sidebar: 7 drip templates with Lucide icons, modern card-list style
 * - Center: subject / body editor, active toggle, delay field, send-test button
 * - Right: Template Variables panel with full CRUD (add / edit / delete)
 *   Built-in vars (isBuiltIn) can be edited but not deleted.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  MailOpen, RefreshCw, TrendingUp, Flag, CalendarCheck,
  ClipboardCheck, FileText, Save, Send, Eye, Edit3, Loader2,
  CheckCircle2, Plus, Pencil, Trash2, Hash, ListOrdered, X,
  Clock, ShieldCheck, Wrench, GripVertical, Mail, Star, StarOff, Layers,
} from 'lucide-react';
import { Button }            from '@/components/ui/button';
import { Input }             from '@/components/ui/input';
import { Label }             from '@/components/ui/label';
import { Badge }             from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea }          from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { cn }           from '@/lib/utils';
import { toast }        from 'sonner';
import { PageHeader }   from '@/components/ux';
import {
  getProspectEmailTemplates,
  createProspectEmailTemplate,
  updateProspectEmailTemplate,
  deleteProspectEmailTemplate,
  reorderProspectDripTemplates,
  sendTestProspectEmail,
  getProspectTemplateVariables,
  createProspectTemplateVariable,
  updateProspectTemplateVariable,
  deleteProspectTemplateVariable,
  getProspectDripSequences,
  createProspectDripSequence,
  deleteProspectDripSequence,
  setDefaultDripSequence,
} from '@/services/apiClient';

// ── Template sidebar metadata ─────────────────────────────────────────────────
const TYPE_META = {
  cold_outreach:     { label: 'Cold Outreach',         Icon: MailOpen,       step: '1', badge: 'Drip Step 1',  badgeCls: 'bg-violet-500/10 text-violet-600 border-violet-500/20', headerCls: 'bg-violet-500/5',  stepCls: 'bg-violet-500'  },
  followup_1:        { label: 'Follow-Up #1',          Icon: RefreshCw,      step: '2', badge: 'Day 3',         badgeCls: 'bg-purple-500/10 text-purple-600 border-purple-500/20', headerCls: 'bg-purple-500/5',  stepCls: 'bg-purple-500'  },
  followup_2:        { label: 'Follow-Up #2',          Icon: TrendingUp,     step: '3', badge: 'Day 7',         badgeCls: 'bg-fuchsia-500/10 text-fuchsia-600 border-fuchsia-500/20', headerCls: 'bg-fuchsia-500/5', stepCls: 'bg-fuchsia-500' },
  followup_3:        { label: 'Follow-Up #3 (Final)',  Icon: Flag,           step: '4', badge: 'Day 12',        badgeCls: 'bg-pink-500/10 text-pink-600 border-pink-500/20',    headerCls: 'bg-pink-500/5',    stepCls: 'bg-pink-500'    },
  demo_request:      { label: 'Demo Scheduling',       Icon: CalendarCheck,  step: null, badge: 'After Reply',  badgeCls: 'bg-sky-500/10 text-sky-600 border-sky-500/20',       headerCls: 'bg-sky-500/5',     stepCls: 'bg-sky-500'     },
  post_demo:         { label: 'Post-Demo Follow-Up',   Icon: ClipboardCheck, step: null, badge: 'After Demo',   badgeCls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20', headerCls: 'bg-emerald-500/5', stepCls: 'bg-emerald-500' },
  proposal_followup: { label: 'Proposal Follow-Up',    Icon: FileText,       step: null, badge: 'After Proposal', badgeCls: 'bg-amber-500/10 text-amber-600 border-amber-500/20', headerCls: 'bg-amber-500/5',  stepCls: 'bg-amber-500'   },
};

// ── Preview example substitution ──────────────────────────────────────────────
function applyPreview(text, variables) {
  let out = text || '';
  variables.forEach(v => {
    const re = new RegExp(v.key.replace(/[{}]/g, '\\$&'), 'g');
    out = out.replace(re, v.example || v.label || v.key);
  });
  return out;
}

// ── Toggle switch ──────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        checked ? 'bg-emerald-500' : 'bg-input',
      )}
    >
      <span className={cn(
        'pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform',
        checked ? 'translate-x-4' : 'translate-x-0',
      )} />
    </button>
  );
}

// ── Loading skeleton ───────────────────────────────────────────────────────────
function PageSkeleton() {
  return (
    <div className="flex h-[calc(100vh-11rem)] animate-pulse">
      <aside className="w-64 shrink-0 border-r border-border space-y-px p-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-14 rounded-md bg-muted" />
        ))}
      </aside>
      <div className="flex-1 p-6 space-y-4">
        <div className="h-8 w-48 rounded bg-muted" />
        <div className="h-10 rounded bg-muted" />
        <div className="h-52 rounded bg-muted" />
        <div className="flex gap-3">
          <div className="h-10 w-32 rounded bg-muted" />
          <div className="h-10 w-40 rounded bg-muted" />
        </div>
      </div>
      <aside className="w-64 shrink-0 border-l border-border p-3 space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-12 rounded bg-muted" />
        ))}
      </aside>
    </div>
  );
}

// ── Variable Dialog (add / edit) ──────────────────────────────────────────────
const EMPTY_VAR_FORM = { key: '', label: '', description: '', example: '', staticValue: '' };

function VariableDialog({ open, onClose, editing, onSaved }) {
  const isNew     = !editing;
  const isBuiltIn = editing?.isBuiltIn;

  const [form, setForm] = useState(EMPTY_VAR_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing
        ? { key: editing.key, label: editing.label, description: editing.description || '', example: editing.example || '', staticValue: editing.staticValue || '' }
        : EMPTY_VAR_FORM
      );
    }
  }, [open, editing]);

  function sf(k, v) { setForm(f => ({ ...f, [k]: v })); }

  const handleSubmit = async () => {
    if (!form.label.trim()) { toast.error('Label is required.'); return; }
    if (isNew && !form.key.trim()) { toast.error('Variable key is required.'); return; }
    setSaving(true);
    try {
      let result;
      if (isNew) {
        result = await createProspectTemplateVariable(form);
      } else {
        result = await updateProspectTemplateVariable(editing._id, form);
      }
      onSaved(result.data, isNew);
      toast.success(isNew ? 'Variable created!' : 'Variable updated!');
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add Variable' : isBuiltIn ? 'Edit Built-in Variable' : 'Edit Variable'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Key */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Variable Key
            </Label>
            {isBuiltIn ? (
              <div className="flex items-center gap-2">
                <code className="block w-full rounded-md border border-border bg-muted px-3 py-2 text-sm font-mono text-primary">
                  {form.key}
                </code>
                <Badge variant="outline" className="shrink-0 bg-violet-500/10 text-violet-600 border-violet-500/20 text-[10px]">
                  <ShieldCheck className="mr-1 h-3 w-3" />built-in
                </Badge>
              </div>
            ) : (
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-muted-foreground">{'{{ }}'}</span>
                <Input
                  value={form.key.replace(/[{}]/g, '')}
                  onChange={e => sf('key', e.target.value.replace(/[{}]/g, '').replace(/\s+/g, '_').toLowerCase())}
                  placeholder="my_variable"
                  className="pl-10 font-mono"
                  disabled={!isNew}
                />
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              {isBuiltIn ? 'Built-in keys are resolved from lead data and cannot be changed.' : isNew ? 'Use lowercase + underscores. Braces are added automatically.' : 'Key cannot be changed after creation.'}
            </p>
          </div>

          {/* Label */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Label</Label>
            <Input value={form.label} onChange={e => sf('label', e.target.value)} placeholder="e.g. First Name" />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Description</Label>
            <Input value={form.description} onChange={e => sf('description', e.target.value)} placeholder="Short tooltip shown in the cheatsheet" />
          </div>

          {/* Example (preview) */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Example Value <span className="normal-case font-normal">(used in preview mode)</span>
            </Label>
            <Input value={form.example} onChange={e => sf('example', e.target.value)} placeholder="e.g. Shruti" />
          </div>

          {/* Static value — custom vars only */}
          {!isBuiltIn && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Static Value <span className="normal-case font-normal">(substituted in actual emails)</span>
              </Label>
              <Input value={form.staticValue} onChange={e => sf('staticValue', e.target.value)} placeholder="Value inserted when email is sent" />
              <p className="text-[11px] text-muted-foreground">
                Unlike built-in variables this does not read from lead data — it replaces with this fixed text.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : isNew ? 'Create Variable' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete confirm dialog ──────────────────────────────────────────────────────
function DeleteVarDialog({ variable, templates, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  // Find templates that use this variable
  const usedIn = useMemo(() => {
    if (!variable) return [];
    const pattern = `{{${variable.key}}}`;
    return (templates || []).filter(t =>
      (t.subject || '').includes(pattern) || (t.body || '').includes(pattern)
    );
  }, [variable, templates]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteProspectTemplateVariable(variable._id);
      onDeleted(variable._id);
      toast.success(`Variable {{${variable.key}}} deleted.`);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to delete.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!variable} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Variable?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            Permanently remove <code className="font-mono text-foreground bg-muted px-1 rounded">{'{{' + (variable?.key || '') + '}}'}</code> from the variable cheatsheet.
          </p>
          {usedIn.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1">
                <span>⚠</span> Used in {usedIn.length} template{usedIn.length > 1 ? 's' : ''}
              </p>
              <ul className="text-xs text-amber-600 dark:text-amber-300 list-disc list-inside space-y-0.5">
                {usedIn.map(t => <li key={t._id}>{t.name || t.type}</li>)}
              </ul>
              <p className="text-xs text-amber-600 dark:text-amber-300 mt-1">
                Those templates will send the raw placeholder text after deletion.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</> : usedIn.length > 0 ? 'Delete Anyway' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
// ── Create Template Dialog ─────────────────────────────────────────────────────
function CreateTemplateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', type: '', subject: '', body: '', delayDays: 0, isDrip: false });
  const [saving, setSaving] = useState(false);
  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Name is required.'); return; }
    setSaving(true);
    try {
      const type = form.type.trim() || autoSlug(form.name);
      const res = await createProspectEmailTemplate({ ...form, type });
      toast.success('Template created!');
      onCreated(res.data);
      onClose();
      setForm({ name: '', type: '', subject: '', body: '', delayDays: 0, isDrip: false });
    } catch (e) { toast.error(e.message || 'Failed to create.'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> New Email Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Template Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value, type: f.type || autoSlug(e.target.value) }))} placeholder="e.g. Re-engagement" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Type / Slug</Label>
              <Input value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} placeholder={autoSlug(form.name) || 'auto'} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Subject</Label>
            <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Email subject…" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Body</Label>
            <Textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={6} placeholder="Email body with {{variables}}…" className="font-mono text-sm" />
          </div>
          <div className="flex items-center gap-4">
            <div className="space-y-1.5 flex-1">
              <Label className="text-xs font-semibold flex items-center gap-1"><Clock className="h-3 w-3" /> Delay (days)</Label>
              <Input type="number" min="0" max="60" value={form.delayDays} onChange={e => setForm(f => ({ ...f, delayDays: Number(e.target.value) }))} className="w-24" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none pt-5">
              <input type="checkbox" className="h-4 w-4 rounded border" checked={form.isDrip}
                onChange={e => setForm(f => ({ ...f, isDrip: e.target.checked }))} />
              <span className="text-sm font-medium">Part of drip sequence</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete Template Dialog ─────────────────────────────────────────────────────
function DeleteTemplateDialog({ template, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteProspectEmailTemplate(template.type);
      toast.success('Template deleted.');
      onDeleted(template.type);
      onClose();
    } catch (e) { toast.error(e.message || 'Failed to delete.'); }
    finally { setDeleting(false); }
  };
  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive"><Trash2 className="h-4 w-4" /> Delete Template</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground py-2">
          Permanently delete <strong>{template.name || template.type}</strong>? This cannot be undone.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="gap-1.5">
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Drip Sequences Manager Panel ──────────────────────────────────────────────
function DripSequencesPanel({ onClose }) {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [creating, setCreating]   = useState(false);
  const [newName, setNewName]     = useState('');
  const [newDesc, setNewDesc]     = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await getProspectDripSequences();
      setSequences(res.data || []);
    } catch (e) { toast.error('Failed to load sequences.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Name is required.'); return; }
    try {
      const res = await createProspectDripSequence({ name: newName.trim(), description: newDesc.trim() });
      setSequences(prev => [...prev, res.data]);
      setNewName(''); setNewDesc(''); setCreating(false);
      toast.success('Sequence created!');
    } catch (e) { toast.error(e.message || 'Failed to create.'); }
  };

  const handleDelete = async (seq) => {
    if (seq.isDefault) { toast.error('Cannot delete the default sequence.'); return; }
    try {
      await deleteProspectDripSequence(seq._id);
      setSequences(prev => prev.filter(s => s._id !== seq._id));
      toast.success('Sequence deleted.');
    } catch (e) { toast.error(e.message || 'Failed to delete.'); }
  };

  const handleSetDefault = async (seq) => {
    try {
      await setDefaultDripSequence(seq._id);
      setSequences(prev => prev.map(s => ({ ...s, isDefault: s._id === seq._id })));
      toast.success(`"${seq.name}" set as default.`);
    } catch (e) { toast.error(e.message || 'Failed.'); }
  };

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-violet-500" /> Drip Sequences
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : sequences.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No sequences yet. Create one below.</p>
          ) : sequences.map(seq => (
            <div key={seq._id} className="flex items-start gap-2 rounded-lg border border-border p-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-semibold">{seq.name}</span>
                  {seq.isDefault && <Badge className="text-[10px] px-1.5 h-4 bg-violet-500/10 text-violet-600 border-violet-500/20">Default</Badge>}
                  {seq.isActive === false && <Badge variant="secondary" className="text-[10px] px-1.5 h-4">Inactive</Badge>}
                </div>
                {seq.description && <p className="text-xs text-muted-foreground mt-0.5">{seq.description}</p>}
                <p className="text-xs text-muted-foreground mt-0.5">{(seq.steps || []).length} steps</p>
              </div>
              <div className="flex gap-1 shrink-0">
                {!seq.isDefault && (
                  <Button size="icon" variant="ghost" className="h-7 w-7" title="Set as default" onClick={() => handleSetDefault(seq)}>
                    <Star className="h-3.5 w-3.5" />
                  </Button>
                )}
                {!seq.isDefault && (
                  <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-destructive hover:bg-destructive/10" title="Delete" onClick={() => handleDelete(seq)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {creating ? (
          <div className="space-y-2 border-t pt-3">
            <Input placeholder="Sequence name" value={newName} onChange={e => setNewName(e.target.value)} autoFocus />
            <Input placeholder="Description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => { setCreating(false); setNewName(''); setNewDesc(''); }}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Create</Button>
            </div>
          </div>
        ) : (
          <div className="border-t pt-3">
            <Button size="sm" variant="outline" className="w-full gap-1.5" onClick={() => setCreating(true)}>
              <Plus className="h-3.5 w-3.5" /> New Drip Sequence
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SuperAdminEmailTemplatesPage() {
  const [templates, setTemplates]     = useState([]);
  const [selected, setSelected]       = useState(null);
  const [form, setForm]               = useState({});
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [testing, setTesting]         = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail]     = useState('');
  const [dirty, setDirty]             = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Variables state
  const [variables, setVariables]     = useState([]);
  const [varDialog, setVarDialog]     = useState(null);
  const [deleteVar, setDeleteVar]     = useState(null);

  // Template CRUD + reorder state
  const [showCreate, setShowCreate]       = useState(false);
  const [deleteTmpl, setDeleteTmpl]       = useState(null);
  const [dragOver, setDragOver]           = useState(null);
  const [showSequences, setShowSequences] = useState(false);
  const dragSrcType                   = useRef(null);

  // ── Load data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [tmplRes, varRes] = await Promise.all([
          getProspectEmailTemplates(),
          getProspectTemplateVariables(),
        ]);

        const list = (tmplRes.data || []).sort((a, b) => {
          const order = Object.keys(TYPE_META);
          return order.indexOf(a.type) - order.indexOf(b.type);
        });
        setTemplates(list);
        if (list.length) pick(list[0], list);

        setVariables(varRes.data || []);
      } catch (e) {
        toast.error('Failed to load: ' + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Select a template ──────────────────────────────────────────────────────
  function pick(tmpl, list) {
    const all  = list || templates;
    const type = typeof tmpl === 'string' ? tmpl : tmpl.type;
    const t    = all.find(x => x.type === type) || tmpl;
    setSelected(t.type);
    setForm({
      name:        t.name        || '',
      description: t.description || '',
      subject:     t.subject     || '',
      body:        t.body        || '',
      delayDays:   t.delayDays   != null ? t.delayDays : 0,
      isActive:    t.isActive    !== false,
    });
    setDirty(false);
    setPreviewMode(false);
  }

  function handlePick(tmpl) {
    if (!dirty || window.confirm('Unsaved changes will be lost. Continue?')) pick(tmpl);
  }

  function setField(key, val) {
    setForm(f => ({ ...f, [key]: val }));
    setDirty(true);
  }

  // ── Save template ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selected || !dirty) return;
    setSaving(true);
    try {
      const res = await updateProspectEmailTemplate(selected, form);
      setTemplates(prev => prev.map(t => t.type === selected ? res.data : t));
      setDirty(false);
      toast.success('Template saved!');
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Send test ──────────────────────────────────────────────────────────────
  const handleTest = () => {
    if (!selected || testing) return;
    setTestEmail('');
    setTestDialogOpen(true);
  };

  const handleSendTest = async () => {
    if (!testEmail.trim() || testing) return;
    setTesting(true);
    try {
      const res = await sendTestProspectEmail(selected, testEmail.trim());
      toast.success(res.message || `Test email sent to ${testEmail.trim()}!`);
      setTestDialogOpen(false);
    } catch (e) {
      toast.error('Test failed: ' + e.message);
    } finally {
      setTesting(false);
    }
  };

  // ── Insert variable at cursor ──────────────────────────────────────────────
  function insertVar(key) {
    const ta = document.getElementById('tmpl-body');
    if (!ta) { setField('body', form.body + key); return; }
    const s = ta.selectionStart, e = ta.selectionEnd;
    setField('body', form.body.slice(0, s) + key + form.body.slice(e));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(s + key.length, s + key.length); }, 0);
  }

  // ── Variable CRUD callbacks ────────────────────────────────────────────────
  function handleVarSaved(doc, isNew) {
    setVariables(prev => isNew
      ? [...prev, doc].sort((a, b) => a.order - b.order)
      : prev.map(v => v._id === doc._id ? doc : v)
    );
  }
  function handleVarDeleted(id) {
    setVariables(prev => prev.filter(v => v._id !== id));
  }

  // ── Template CRUD handlers ─────────────────────────────────────────────────
  function handleTemplateCreated(doc) {
    setTemplates(prev => [...prev, doc].sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99)));
    pick(doc, [...templates, doc]);
  }

  function handleTemplateDeleted(type) {
    const next = templates.filter(t => t.type !== type);
    setTemplates(next);
    if (selected === type) { if (next.length) pick(next[0], next); else setSelected(null); }
  }

  // ── Drip reorder (drag-and-drop) ──────────────────────────────────────────
  const dripTemplates = templates.filter(t => t.isDrip);

  async function handleDrop(targetType) {
    const srcType = dragSrcType.current;
    if (!srcType || srcType === targetType) { setDragOver(null); return; }
    // Build new order: swap src into target position
    const types = dripTemplates.map(t => t.type);
    const from  = types.indexOf(srcType);
    const to    = types.indexOf(targetType);
    if (from < 0 || to < 0) { setDragOver(null); return; }
    types.splice(from, 1);
    types.splice(to, 0, srcType);
    setDragOver(null);
    try {
      const res = await reorderProspectDripTemplates(types);
      setTemplates(res.data);
      toast.success('Drip order updated.');
    } catch (e) { toast.error(e.message || 'Reorder failed.'); }
  }

  // ── Preview text ───────────────────────────────────────────────────────────
  const preview = useCallback(() => ({
    subject: applyPreview(form.subject, variables),
    body:    applyPreview(form.body,    variables),
  }), [form.subject, form.body, variables]);

  const selectedTmpl = templates.find(t => t.type === selected);
  const meta = selected ? (TYPE_META[selected] || { label: selectedTmpl?.name || selected, Icon: Mail, step: selectedTmpl?.isDrip ? '?' : null, badge: selectedTmpl?.isDrip ? 'Drip' : 'Manual', badgeCls: 'bg-muted text-muted-foreground border-border', headerCls: 'bg-muted/30', stepCls: 'bg-muted-foreground' }) : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">

      <PageHeader
        title="Email Templates"
        description="Edit the outreach emails your drip sequence sends. Drag drip steps to reorder."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowSequences(true)} className="gap-1.5">
              <Layers className="h-3.5 w-3.5" /> Sequences
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowCreate(true)} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" /> New Template
            </Button>
            {meta && (
              <>
                <Button variant="outline" size="sm" onClick={handleTest} disabled={testing}>
                  {testing
                    ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Sending…</>
                    : <><Send className="mr-2 h-3.5 w-3.5" />Send Test</>}
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving || !dirty}>
                  {saving
                    ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Saving…</>
                    : dirty
                      ? <><Save className="mr-2 h-3.5 w-3.5" />Save Changes</>
                      : <><CheckCircle2 className="mr-2 h-3.5 w-3.5" />Saved</>}
                </Button>
              </>
            )}
          </div>
        }
      />

      {loading ? (
        <PageSkeleton />
      ) : (
        <div className="flex flex-1 min-h-0 h-[calc(100vh-10.5rem)]">

          {/* ── Sidebar ────────────────────────────────────────────────────── */}
          <aside className="w-64 shrink-0 border-r border-border bg-background overflow-y-auto">
            {/* Drip sequence section with drag-to-reorder */}
            {dripTemplates.length > 0 && (
              <div className="border-b border-border/60">
                <div className="flex items-center gap-1.5 px-4 py-2 bg-muted/30">
                  <ListOrdered className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Drip Sequence</span>
                  <span className="text-[9px] text-muted-foreground/60 ml-auto">drag to reorder</span>
                </div>
                {dripTemplates.map((t, idx) => {
                  const m     = TYPE_META[t.type] || {};
                  const isSel = t.type === selected;
                  const Icon  = m.Icon || Mail;
                  const isOver = dragOver === t.type;
                  return (
                    <div
                      key={t.type}
                      draggable
                      onDragStart={() => { dragSrcType.current = t.type; }}
                      onDragOver={e => { e.preventDefault(); setDragOver(t.type); }}
                      onDragLeave={() => setDragOver(null)}
                      onDrop={() => handleDrop(t.type)}
                      onDragEnd={() => { dragSrcType.current = null; setDragOver(null); }}
                      className={cn(
                        'flex items-center gap-2 px-3 py-3 border-b border-border/40 border-l-2 transition-colors cursor-grab active:cursor-grabbing group',
                        isSel   ? 'border-l-primary bg-primary/5' : 'border-l-transparent hover:bg-muted/50',
                        isOver  ? 'bg-violet-500/10 border-l-violet-500' : '',
                      )}
                    >
                      <GripVertical className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0 group-hover:text-muted-foreground" />
                      <span className={cn('flex h-5 w-5 items-center justify-center rounded-full text-white text-[10px] font-bold shrink-0', m.stepCls || 'bg-muted-foreground')}>
                        {idx + 1}
                      </span>
                      <button className="flex-1 min-w-0 text-left" onClick={() => handlePick(t)}>
                        <p className={cn('text-sm truncate leading-none mb-1', isSel ? 'font-semibold text-foreground' : 'font-medium text-foreground/80')}>
                          {m.label || t.name || t.type}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={cn('text-[9px] px-1.5 py-0 h-3.5 leading-none', m.badgeCls || 'bg-muted text-muted-foreground border-border')}>
                            {t.delayDays > 0 ? `Day +${t.delayDays}` : 'Immediate'}
                          </Badge>
                          {!t.isActive && <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-3.5 bg-red-500/10 text-red-600 border-red-500/20">Paused</Badge>}
                          {dirty && isSel && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Non-drip templates */}
            {templates.filter(t => !t.isDrip).length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 px-4 py-2 bg-muted/30">
                  <Mail className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Manual Templates</span>
                </div>
                {templates.filter(t => !t.isDrip).map(t => {
                  const m    = TYPE_META[t.type] || {};
                  const isSel = t.type === selected;
                  const Icon  = m.Icon || Mail;
                  return (
                    <div key={t.type} className={cn('flex items-center gap-2 px-3 py-3 border-b border-border/40 border-l-2 transition-colors group',
                      isSel ? 'border-l-primary bg-primary/5' : 'border-l-transparent hover:bg-muted/50')}>
                      <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', isSel ? 'bg-primary/10' : 'bg-muted')}>
                        <Icon className={cn('h-3.5 w-3.5', isSel ? 'text-primary' : 'text-muted-foreground')} />
                      </span>
                      <button className="flex-1 min-w-0 text-left" onClick={() => handlePick(t)}>
                        <p className={cn('text-sm truncate leading-none mb-1', isSel ? 'font-semibold text-foreground' : 'font-medium text-foreground/80')}>
                          {m.label || t.name || t.type}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={cn('text-[9px] px-1.5 py-0 h-3.5 leading-none', m.badgeCls || 'bg-muted text-muted-foreground border-border')}>
                            {m.badge || 'Manual'}
                          </Badge>
                          {!t.isActive && <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-3.5 bg-red-500/10 text-red-600 border-red-500/20">Paused</Badge>}
                          {dirty && isSel && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />}
                        </div>
                      </button>
                      {!t.isBuiltIn && (
                        <button onClick={() => setDeleteTmpl(t)} className="opacity-0 group-hover:opacity-100 p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </aside>

          {/* ── Editor + Variable panel ─────────────────────────────────────── */}
          {selected && meta ? (
            <div className="flex-1 overflow-y-auto p-6 flex gap-5 items-start">

              {/* Main editor */}
              <div className="flex-1 min-w-0">
                <Card className="overflow-hidden">
                  {/* Header strip */}
                  <div className={cn('px-5 py-4 border-b border-border flex items-center justify-between', meta.headerCls)}>
                    <div className="flex items-center gap-3">
                      <span className={cn('flex h-9 w-9 items-center justify-center rounded-lg', meta.headerCls)}>
                        {meta.Icon && <meta.Icon className="h-5 w-5 text-foreground/70" />}
                      </span>
                      <div>
                        <p className="font-semibold text-foreground leading-none">{meta.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{form.description || 'No description'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedTmpl && !selectedTmpl.isBuiltIn && (
                        <button onClick={() => setDeleteTmpl(selectedTmpl)}
                          className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-destructive transition-colors px-2 py-1 rounded hover:bg-destructive/10">
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                      )}
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <Toggle checked={form.isActive} onChange={() => setField('isActive', !form.isActive)} />
                        <span className={cn('text-xs font-medium', form.isActive ? 'text-emerald-600' : 'text-muted-foreground')}>
                          {form.isActive ? 'Active' : 'Paused'}
                        </span>
                      </label>
                    </div>
                  </div>

                  <CardContent className="p-5 space-y-4">
                    {/* Name + delay */}
                    <div className="flex gap-3 flex-wrap">
                      <div className="flex-[2] min-w-[180px] space-y-1.5">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Template Name</Label>
                        <Input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Template name…" />
                      </div>
                      {meta.step && (
                        <div className="flex-none w-[150px] space-y-1.5">
                          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />Delay (days)
                          </Label>
                          <Input type="number" min="0" max="30" value={form.delayDays} onChange={e => setField('delayDays', Number(e.target.value))} />
                        </div>
                      )}
                    </div>

                    {/* Subject */}
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email Subject</Label>
                      <Input value={form.subject} onChange={e => setField('subject', e.target.value)} placeholder="Subject line…" />
                    </div>

                    {/* Body */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email Body</Label>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setPreviewMode(p => !p)}>
                          {previewMode
                            ? <><Edit3 className="mr-1.5 h-3 w-3" />Edit</>
                            : <><Eye    className="mr-1.5 h-3 w-3" />Preview</>}
                        </Button>
                      </div>

                      {previewMode ? (
                        <div className="rounded-md border border-border bg-muted/30 p-4 min-h-[220px] text-sm leading-relaxed text-foreground whitespace-pre-wrap font-mono">
                          <p className="text-xs text-muted-foreground border-b border-border pb-2 mb-3 font-sans">
                            <span className="font-semibold">Subject:</span> {preview().subject}
                          </p>
                          {preview().body}
                        </div>
                      ) : (
                        <Textarea
                          id="tmpl-body"
                          value={form.body}
                          onChange={e => setField('body', e.target.value)}
                          rows={12}
                          className="font-mono text-sm resize-y leading-relaxed"
                          placeholder="Write the email body here…"
                          spellCheck
                        />
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2.5 pt-1">
                      <Button onClick={handleSave} disabled={saving || !dirty} className="flex-1 min-w-[120px]">
                        {saving
                          ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
                          : dirty
                            ? <><Save className="mr-2 h-4 w-4" />Save Changes</>
                            : <><CheckCircle2 className="mr-2 h-4 w-4" />Saved</>}
                      </Button>
                      <Button variant="outline" onClick={handleTest} disabled={testing}>
                        {testing
                          ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</>
                          : <><Send className="mr-2 h-4 w-4" />Send Test Email</>}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Test email is sent to <strong>devanshdixit97@gmail.com</strong> with sample lead data.</p>
                  </CardContent>
                </Card>
              </div>

              {/* ── Variable cheatsheet + CRUD ────────────────────────────── */}
              <aside className="w-64 shrink-0 sticky top-6">
                <Card>
                  <CardContent className="p-0">

                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                      <div className="flex items-center gap-2">
                        <Hash className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-semibold text-foreground">Variables</span>
                        <Badge variant="secondary" className="text-[10px] px-1.5 h-4">{variables.length}</Badge>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs gap-1"
                        onClick={() => setVarDialog('new')}
                      >
                        <Plus className="h-3 w-3" />Add
                      </Button>
                    </div>

                    <p className="px-4 py-2 text-[11px] text-muted-foreground border-b border-border/50 leading-relaxed">
                      Click a variable to insert at cursor.{' '}
                      <span className="inline-flex items-center gap-0.5"><ShieldCheck className="h-2.5 w-2.5" /> = built-in, <Wrench className="h-2.5 w-2.5" /> = custom.</span>
                    </p>

                    {/* Variable list */}
                    <div className="overflow-y-auto max-h-[420px]">
                      {variables.map(v => (
                        <div
                          key={v._id}
                          className="group flex items-start gap-2 px-3 py-2.5 border-b border-border/40 last:border-0 hover:bg-muted/40 transition-colors"
                        >
                          {/* Click area → insert */}
                          <button
                            className="flex-1 min-w-0 text-left"
                            onClick={() => insertVar(v.key)}
                          >
                            <div className="flex items-center gap-1.5">
                              <code className="text-[11px] font-mono text-primary font-semibold leading-none">
                                {v.key}
                              </code>
                              {v.isBuiltIn
                                ? <ShieldCheck className="h-2.5 w-2.5 text-violet-500 shrink-0" />
                                : <Wrench      className="h-2.5 w-2.5 text-amber-500 shrink-0" />}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                              {v.description || v.label}
                            </p>
                          </button>

                          {/* Edit / Delete */}
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 pt-0.5">
                            <button
                              className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                              title="Edit"
                              onClick={() => setVarDialog(v)}
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                            <button
                              className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                              title="Delete"
                              onClick={() => setDeleteVar(v)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {variables.length === 0 && (
                        <p className="px-4 py-6 text-center text-xs text-muted-foreground">No variables yet.</p>
                      )}
                    </div>

                    {/* Drip order quick-nav */}
                    <div className="border-t border-border px-4 py-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <ListOrdered className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Drip Order</span>
                      </div>
                      <div className="space-y-1.5">
                        {Object.entries(TYPE_META).filter(([, m]) => m.step).map(([type, m]) => (
                          <button
                            key={type}
                            onClick={() => handlePick({ type })}
                            className="w-full flex items-center gap-2 text-left group"
                          >
                            <span className={cn('h-5 w-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center shrink-0', m.stepCls)}>
                              {m.step}
                            </span>
                            <div className="min-w-0">
                              <p className={cn('text-[11px] font-medium truncate', type === selected ? 'text-foreground' : 'text-foreground/60 group-hover:text-foreground')}>
                                {m.label}
                              </p>
                              <p className="text-[10px] text-muted-foreground">{m.badge}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                  </CardContent>
                </Card>
              </aside>

            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              Select a template to edit.
            </div>
          )}

        </div>
      )}

      {/* ── Dialogs ─────────────────────────────────────────────────────────── */}
      <VariableDialog
        open={!!varDialog}
        editing={varDialog === 'new' ? null : varDialog}
        onClose={() => setVarDialog(null)}
        onSaved={handleVarSaved}
      />

      {deleteVar && (
        <DeleteVarDialog
          variable={deleteVar}
          templates={templates}
          onClose={() => setDeleteVar(null)}
          onDeleted={handleVarDeleted}
        />
      )}

      <CreateTemplateDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleTemplateCreated}
      />

      {deleteTmpl && (
        <DeleteTemplateDialog
          template={deleteTmpl}
          onClose={() => setDeleteTmpl(null)}
          onDeleted={handleTemplateDeleted}
        />
      )}

      {showSequences && <DripSequencesPanel onClose={() => setShowSequences(false)} />}

      {/* ── Send Test Email Dialog ── */}
      <Dialog open={testDialogOpen} onOpenChange={v => !v && setTestDialogOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-4 w-4 text-primary" /> Send Test Email
            </DialogTitle>
            <DialogDescription>
              Enter the email address where you'd like to receive the test. We'll send a rendered preview of <strong>{selected}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="space-y-1.5">
              <Label htmlFor="test-email-input">Recipient email</Label>
              <Input
                id="test-email-input"
                type="email"
                placeholder="you@example.com"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSendTest()}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setTestDialogOpen(false)} disabled={testing}>
                Cancel
              </Button>
              <Button onClick={handleSendTest} disabled={testing || !testEmail.trim()} className="gap-2">
                {testing
                  ? <><Loader2 className="h-4 w-4 animate-spin" />Sending…</>
                  : <><Send className="h-4 w-4" />Send Test</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
